// Payroll — /api/hr/payroll
// Kenyan statutory payroll (PAYE, NSSF, SHIF, Affordable Housing Levy).
// Backend guide: 2026-07-15. No frontend existed before this module.

// ─── Enums ────────────────────────────────────────────────────────────────────

export type PayrollPeriodStatus =
  | "DRAFT"
  | "IMPORTED"
  | "CALCULATED"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "REJECTED"
  | "PAID";

// Exact values for the import-batch lifecycle weren't enumerated in the backend guide
// (only "validated" vs "posted" were described in prose). Modeled on Inventory's/Asset's
// batch-status shape with a string fallback so an unexpected value still renders instead
// of silently falling through — confirm the literal set with backend if exact styling matters.
export type PayrollImportBatchStatus =
  | "VALIDATED"
  | "PARTIALLY_VALID"
  | "FAILED"
  | "POSTED"
  | string;

export type PayrollLineType = "STATUTORY" | "OTHER" | "BENEFIT";

// ─── Core models ──────────────────────────────────────────────────────────────

/** GET /periods, /periods/{id} */
export type PayrollPeriod = {
  id: number;
  periodCode: string;
  periodYear: number;
  periodMonth: number;
  status: PayrollPeriodStatus;
  workflowCode?: string | null;
  currentStepOrder?: number | null;
  currentStepName?: string | null;
  currentApproverUserId?: number | null;
  currentApproverName?: string | null;
  rejectionReason?: string | null;
  preparedByUsername?: string | null;
  calculatedAt?: string | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  paidAt?: string | null;
};

/** GET /periods/{periodId}/import (list) */
export type PayrollImportBatch = {
  id: number;
  periodId?: number;
  batchNo?: string | null;
  status: PayrollImportBatchStatus;
  originalFileName?: string | null;
  totalRows?: number;
  validRows?: number;
  invalidRows?: number;
  uploadedByUsername?: string | null;
  uploadedAt?: string | null;
  postedAt?: string | null;
  remarks?: string | null;
};

/** GET /import/{batchId}/rows */
export type PayrollImportRow = {
  id: number;
  rowNumber: number;
  employeeNumber: string | null;
  employeeName?: string | null;
  basicPay: number | null;
  houseAllowance?: number | null;
  transportAllowance?: number | null;
  otherAllowance?: number | null;
  validRow: boolean;
  errorMessage?: string | null;
};

/** GET /periods/{periodId}/salary-records — editable while the period hasn't been submitted yet
 *  (DRAFT/IMPORTED/CALCULATED/REJECTED); locked from PENDING_APPROVAL onward. */
export type SalaryRecord = {
  employeeId: number;
  employeeNumber: string | null;
  employeeName: string | null;
  departmentName?: string | null;
  basicPay: number;
  houseAllowance: number;
  transportAllowance: number;
  otherAllowance: number;
};

/** PATCH /periods/{periodId}/salary-records/{employeeId} */
export type SalaryRecordUpdateRequest = {
  basicPay: number;
  houseAllowance: number;
  transportAllowance: number;
  otherAllowance: number;
};

/** GET /periods/{id}/missing-bank-accounts — employees a Calculate attempt flagged as missing
 *  complete bank details, so the UI can offer an inline fix instead of just an error string. */
export type MissingBankAccount = {
  employeeId: number;
  employeeNumber: string | null;
  employeeName: string | null;
};

/** One PAYE band row inside a StatutoryConfig.
 *  bandOrder is assigned automatically from the row's position in the list
 *  (1-indexed) — the backend sorts bands by bandOrder when calculating, so it
 *  must match the ascending lowerBound order the bands are displayed in. */
export type PayeBand = {
  id?: number;
  bandOrder: number;
  lowerBound: number;
  upperBound?: number | null; // null/undefined = no upper bound (top band)
  ratePercent: number; // e.g. 10 for 10%
};

/** GET /config/statutory, /config/statutory/{id}
 *  Field names match the backend's JSON serialization (Spring Boot maps
 *  snake_case DB columns to these camelCase names). */
export type StatutoryConfig = {
  id: number;
  effectiveFrom: string; // yyyy-MM-dd
  active: boolean;
  personalReliefMonthly: number;
  nssfTier1Limit: number;
  nssfTier2Limit: number;
  nssfRatePercent: number; // stored as percentage (e.g. 6 = 6%), not decimal
  shifRatePercent: number; // stored as percentage (e.g. 2.75 = 2.75%)
  shifMinimumAmount: number;
  housingLevyEmployeeRatePercent: number;
  housingLevyEmployerRatePercent: number;
  notes?: string | null;
  payeBands: PayeBand[];
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

/** POST /config/statutory — MANAGE
 *  Rates are sent as percentages (e.g. 6 for 6%), matching the DB columns. */
export type CreateStatutoryConfigRequest = {
  effectiveFrom: string;
  personalReliefMonthly: number;
  nssfTier1Limit: number;
  nssfTier2Limit: number;
  nssfRatePercent: number;
  shifRatePercent: number;
  shifMinimumAmount: number;
  housingLevyEmployeeRatePercent: number;
  housingLevyEmployerRatePercent: number;
  notes?: string | null;
  payeBands: Omit<PayeBand, "id">[];
};

export type UpdateStatutoryConfigRequest = Partial<CreateStatutoryConfigRequest>;

/** Seeded: BANK_LOAN, SACCO, SALARY_ADVANCE, INSURANCE, UNION_DUES, OTHER */
export type DeductionCategory = {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  active: boolean;
};

export type DeductionCategoryRequest = {
  code: string;
  name: string;
  description?: string;
  active?: boolean;
};

/** A deduction is recurring while active=true and the period's month falls within
 *  [startDate, endDate] (endDate=null = indefinite; startDate==endDate = one-off). */
export type EmployeeDeduction = {
  id: number;
  employeeId: number;
  employeeName?: string | null;
  employeeNumber?: string | null;
  categoryId: number;
  categoryName?: string | null;
  amount: number;
  startDate: string;
  endDate?: string | null;
  active: boolean;
  notes?: string | null;
};

export type EmployeeDeductionRequest = {
  employeeId: number;
  categoryId: number;
  amount: number;
  startDate: string;
  endDate?: string | null;
  notes?: string;
};

export type EmployeeDeductionUpdateRequest = Partial<
  Omit<EmployeeDeductionRequest, "employeeId">
>;

/** Seeded: HOUSE_ALLOWANCE, TRANSPORT_ALLOWANCE, CAR_ALLOWANCE, AIRTIME, RESPONSIBILITY, OTHER */
export type BenefitCategory = {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  active: boolean;
};

export type BenefitCategoryRequest = {
  code: string;
  name: string;
  description?: string;
  active?: boolean;
};

/** A benefit is recurring while active=true and the period's month falls within
 *  [startDate, endDate] (endDate=null = indefinite; startDate==endDate = one-off).
 *  Added on top of gross pay — the mirror-image of EmployeeDeduction. */
export type EmployeeBenefit = {
  id: number;
  employeeId: number;
  employeeName?: string | null;
  employeeNumber?: string | null;
  categoryId: number;
  categoryName?: string | null;
  amount: number;
  startDate: string;
  endDate?: string | null;
  active: boolean;
  notes?: string | null;
};

export type EmployeeBenefitRequest = {
  employeeId: number;
  categoryId: number;
  amount: number;
  startDate: string;
  endDate?: string | null;
  notes?: string;
};

export type EmployeeBenefitUpdateRequest = Partial<
  Omit<EmployeeBenefitRequest, "employeeId">
>;

/** GET/PUT /employees/{employeeId}/bank-account */
export type EmployeeBankAccount = {
  employeeId: number;
  bankName?: string | null;
  branchName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
};

export type EmployeeBankAccountRequest = {
  bankName?: string;
  branchName?: string;
  accountName?: string;
  accountNumber?: string;
};

/** Line-item breakdown row on a payslip (lineType: STATUTORY|OTHER|BENEFIT) */
export type PayslipLine = {
  lineType: PayrollLineType;
  label: string;
  amount: number;
};

/** GET /payslips/{id}, /payslips/mine/period/{periodId}, /payslips/period/{periodId} */
export type Payslip = {
  id: number;
  periodId: number;
  periodCode?: string | null;

  employeeId: number;
  employeeNumber: string;
  employeeName: string;
  departmentName?: string | null;
  jobTitle?: string | null;
  employeeKraPin?: string | null;
  employeeNssfNumber?: string | null;
  employeeNhifNumber?: string | null;

  basicPay: number;
  houseAllowance: number;
  transportAllowance: number;
  otherAllowance: number;
  otherBenefitsTotal: number;
  grossPay: number;

  nssfEmployee: number;
  nssfEmployer: number;
  shifAmount: number;
  housingLevyEmployee: number;
  housingLevyEmployer: number;

  taxablePay: number;
  payeCharged: number;
  personalRelief: number;
  payePayable: number;

  statutoryDeductionsTotal: number;
  otherDeductionsTotal: number;
  totalDeductions: number;
  netPay: number;

  lines: PayslipLine[];
};

// ─── Pagination wrapper ───────────────────────────────────────────────────────

export type PageResponse<T> = {
  content: T[];
  page: number; // 0-indexed
  size: number;
  totalElements: number;
  totalPages: number;
};

// ─── UI helpers ───────────────────────────────────────────────────────────────

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function monthLabel(month?: number | null): string {
  if (!month || month < 1 || month > 12) return "—";
  return MONTH_NAMES[month - 1];
}

/** Lifecycle terminal states — no further period-level action possible */
export const PAYROLL_TERMINAL_STATUSES: PayrollPeriodStatus[] = ["PAID"];